import {
  FeatureLayerGeomType,
  MapDrawerEntityForPoi,
} from 'features/mapping/mapping.types';
import { defer } from 'es-toolkit/compat';
import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { atom, useRecoilState } from 'recoil';

type MapDrawerEntityForPoiState = {
  mapDrawerEntityForPoi: MapDrawerEntityForPoi | null;
};

type UseMapDrawerEntityForPoiState = {
  mapDrawerEntityForPoi: MapDrawerEntityForPoi | null;
  setMapDrawerEntityForPoi: (
    mapDrawerEntityForPoi: MapDrawerEntityForPoi[] | null,
  ) => void;
};

const DEFAULT_MAP_DRAWER_ENTITY_STATE: MapDrawerEntityForPoiState = {
  mapDrawerEntityForPoi: null,
};

export const mapDrawerEntityAtom = atom({
  key: 'DEFAULT_MAP_DRAWER_ENTITY_STATE',
  default: DEFAULT_MAP_DRAWER_ENTITY_STATE,
});

// Higher priority geometry type will get precedence when deciding which entity to select.
const CLICK_PRIORITY: Record<FeatureLayerGeomType, number> = {
  point: 3,
  line: 2,
  polygon: 1,
};

const getClickPriority = (
  mapDrawerEntityForPoi: MapDrawerEntityForPoi | null,
): number => {
  if (!mapDrawerEntityForPoi) return 0;
  if ('layerOrderSlot' in mapDrawerEntityForPoi.metadata) {
    return CLICK_PRIORITY[mapDrawerEntityForPoi.metadata.layerOrderSlot];
  }
  // Not an external layer, give a lower priority (currently [Aug 2025], only private land ownership)
  return 0;
};

/**
 * Custom hook that manages map drawer entity state for POIs.
 * Other map entities' states (e.g., geoevents, alert cameras) are handled by the router.
 */
export const useMapDrawerEntityForPoiState =
  (): UseMapDrawerEntityForPoiState => {
    const [mapDrawerEntityForPoiState, setMapDrawerEntityForPoiState] =
      useRecoilState(mapDrawerEntityAtom);
    const history = useHistory();

    const setMapDrawerEntityForPoi = useCallback(
      (mapDrawerEntityForPoi: MapDrawerEntityForPoi[] | null) => {
        setMapDrawerEntityForPoiState((prev): MapDrawerEntityForPoiState => {
          if (!mapDrawerEntityForPoi || mapDrawerEntityForPoi.length === 0) {
            return { mapDrawerEntityForPoi: null };
          }

          const locationPathname = history.location.pathname;
          if (mapDrawerEntityForPoi && locationPathname !== '/') {
            // Defer navigation to ensure Recoil state updates complete before redirecting
            // Map drawer entity POIs render only on the default map page
            defer(() => history.push('/'));
          }

          if (mapDrawerEntityForPoi.length === 1) {
            return { mapDrawerEntityForPoi: mapDrawerEntityForPoi[0] };
          }

          const sortedByPriority = mapDrawerEntityForPoi.sort((a, b) => {
            const aPriority = getClickPriority(a);
            const bPriority = getClickPriority(b);
            return bPriority - aPriority;
          });

          const topEntity = sortedByPriority[0];

          if (
            getClickPriority(prev.mapDrawerEntityForPoi) >
            getClickPriority(topEntity)
          ) {
            return prev;
          }

          return { mapDrawerEntityForPoi: topEntity };
        });
      },
      [history, setMapDrawerEntityForPoiState],
    );

    return {
      mapDrawerEntityForPoi: mapDrawerEntityForPoiState.mapDrawerEntityForPoi,
      setMapDrawerEntityForPoi,
    };
  };
